import React from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  SearchOffOutlined,
  AddOutlined,
  RefreshOutlined,
  FilterListOutlined,
} from "@mui/icons-material";
import NoRecordFoundIcon from "../../assets/norecordfound.png";
import { PageLoader } from "../PageLoader/PageLoader";

interface NoRecordFoundProps {
  isLoading?: boolean;
  title?: string;
  description?: string;
  showAddButton?: boolean;
  showRefreshButton?: boolean;
  showFilterButton?: boolean;
  onAddClick?: () => void;
  onRefreshClick?: () => void;
  onFilterClick?: () => void;
  addButtonText?: string;
  refreshButtonText?: string;
  filterButtonText?: string;
  icon?: React.ReactNode;
  height?: string | number;
}

export const NoRecordFound: React.FC<NoRecordFoundProps> = ({
  isLoading = false,
  title,
  description = "We couldn't find any records. Please create new record .",
  showAddButton = true,
  showRefreshButton = true,
  showFilterButton = false,
  onAddClick,
  onRefreshClick,
  onFilterClick,
  addButtonText = "Add New Record",
  refreshButtonText = "Refresh",
  filterButtonText = "Adjust Filters",
  icon,
  height = "350px",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <PageLoader loading={isLoading}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          minHeight: height === "350px" ? "60vh" : height,
          width: "100%",
          p: 3,
          // background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            width: "100%",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon */}
          <Box sx={{ mb: 3 }}>
            <img
              src={NoRecordFoundIcon}
              alt="No Record Found"
              style={{ width: "200px", height: "auto", objectFit: "contain" }}
            />
          </Box>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 4,
              lineHeight: 1.6,
              maxWidth: 400,
              mx: "auto",
            }}
          >
            {description}
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {showAddButton && onAddClick && (
              <Button
                variant="contained"
                startIcon={<AddOutlined />}
                onClick={onAddClick}
                sx={{
                  minWidth: 160,
                  height: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: `0 4px 12px ${theme.palette.primary.main}20`,
                  "&:hover": {
                    boxShadow: `0 6px 16px ${theme.palette.primary.main}30`,
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {addButtonText}
              </Button>
            )}

            {showRefreshButton && onRefreshClick && (
              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={onRefreshClick}
                sx={{
                  minWidth: 120,
                  height: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.main + "08",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {refreshButtonText}
              </Button>
            )}

            {showFilterButton && onFilterClick && (
              <Button
                variant="text"
                startIcon={<FilterListOutlined />}
                onClick={onFilterClick}
                sx={{
                  minWidth: 120,
                  height: 44,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: theme.palette.grey[100],
                    color: theme.palette.text.primary,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {filterButtonText}
              </Button>
            )}
          </Box>

          {/* Decorative Elements */}
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "10%",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${theme.palette.primary.main}08 0%, transparent 70%)`,
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "15%",
              left: "10%",
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${theme.palette.secondary.main}08 0%, transparent 70%)`,
              zIndex: 0,
            }}
          />
        </Box>
      </Box>
    </PageLoader>
  );
};
